"use client";

import { toast } from "@/components/atoms/toast";
import { cn } from "@/lib/utils";
import { CheckIcon, Loader2 } from "lucide-react";
import { useState, useTransition } from "react";
import CreateWishlistButton from "./create-wishlist-button";
import { DialogClose } from "@/components/ui/dialog";
import AddToFavForm from "./add-to-fav-form";
import { Button } from "@/components/atoms/buttons";
import { Locales } from "@/lib/i18n";
import { __ } from "@/lib/i18n/functions";
import { useWishlist } from "@/hooks/useWishlist";

type AddToFavProps = {
  productId?: string;
  multipleProductIds?: string[];
  showForm?: boolean;
  locale: Locales;
};

const AddToFav = ({ productId, multipleProductIds, showForm = false, locale }: AddToFavProps) => {
  const [showAddToFavForm, setShowAddToFavForm] = useState(showForm);
  const [addingToListId, setAddingToListId] = useState<string | null>(null);
  const [isPending, startTransition] = useTransition();

  const { lists, addToList, isProductInList } = useWishlist();

  const idsToAdd = multipleProductIds ?? (productId ? [productId] : []);

  const handleClick = (listId: string) => {
    if (!idsToAdd.length) return;

    setAddingToListId(listId);

    startTransition(async () => {
      try {
        const addPromises = idsToAdd.map((id) => addToList(listId, id));
        await Promise.all(addPromises);
        toast.success({
          title:
            idsToAdd.length === 1
              ? __("Product added to list successfully!", locale)
              : __("Products added to list successfully!", locale),
        });
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : typeof error === "string"
              ? error
              : __("Something went wrong. Please try again.", locale);
        toast.error({ title: message });
      } finally {
        setAddingToListId(null);
      }
    });
  };

  if (!lists) return;

  return (
    <>
      <div className="border-primary-light/50 border-t">
        {lists.length === 0 && (
          <div className={cn("border-primary-light/50 border-b px-5 py-[15px]", showAddToFavForm && "opacity-50")}>
            {__("No list created. Create a list to add a product to your favourites", locale)}
          </div>
        )}

        {lists.length >= 1 &&
          lists.map((list, index) => {
            if (!list?.listName?.trim()) return;

            return (
              <div
                key={index}
                className={cn(
                  "border-primary-light/50 animate-in fade-in slide-in-from-bottom-2 flex items-center justify-between border-b px-5 py-[15px] duration-300",
                  showAddToFavForm && "opacity-50 transition-opacity"
                )}
              >
                <div className="text-primary-light text-sm">
                  <h5 className="text-dark-primary inline-block">{list.listName}</h5>
                  <span className="ml-2.5">
                    ({list?.products?.length} {__("Products", locale)})
                  </span>
                  <p className="text-xs">
                    {list?.date?.from} - {list?.date?.to}
                  </p>
                </div>

                {idsToAdd.length > 0 &&
                  (() => {
                    const allInList = idsToAdd.every((id) => isProductInList(list.id, id));

                    return allInList ? (
                      <span className="flex items-center gap-2.5 text-[#239256]">
                        <CheckIcon size={18} />
                        {__("Added", locale)}
                      </span>
                    ) : (
                      <Button
                        variant="secondary"
                        disabled={showAddToFavForm || isPending}
                        onClick={() => handleClick(list.id)}
                        className={cn(
                          "rounded-sm",
                          locale === "nl" && "min-w-[92.66px]",
                          locale === "en" && "min-w-[48.25px]",
                          locale === "fr" && "min-w-[70.4px]"
                        )}
                        size="sm"
                      >
                        {isPending && addingToListId === list.id ? (
                          <Loader2 className="animate-spin" aria-hidden="true" />
                        ) : (
                          __("Add", locale)
                        )}
                      </Button>
                    );
                  })()}
              </div>
            );
          })}

        {!showAddToFavForm ? (
          <div id="buttons" className="font-cabinet-grotesk flex items-center gap-2.5 px-5 py-[15px] max-sm:flex-col">
            <CreateWishlistButton locale={locale} onClick={() => setShowAddToFavForm(true)} />
            <DialogClose asChild>
              <Button size="md" variant="rounded" className="w-full flex-1 rounded-sm text-base font-bold">
                {__("Close", locale)}
              </Button>
            </DialogClose>
          </div>
        ) : (
          <AddToFavForm locale={locale} onsetShowAddToFavForm={setShowAddToFavForm} productId={productId} />
        )}
      </div>
    </>
  );
};

export default AddToFav;
